import {
  Icon,
  Stack,
  Typography,
} from '@ace-clublabs/shared-component-library';
import type React from 'react';
import { useEffect, useRef } from 'react';
import SelectionCardsSkeleton from '@/features/appointments/components/SelectionCardsSkeleton';
import type { Branch } from '@/features/appointments/utils/Branch';

type BranchSelectionListProps = {
  branches: Branch[];
  selectedBranch: Branch | null;
  onSelectBranch: (branch: Branch) => void;
  isLoading?: boolean;
};

const BranchSelectionList: React.FC<BranchSelectionListProps> = ({
  branches,
  selectedBranch,
  onSelectBranch,
  isLoading = false,
}) => {
  const branchRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  useEffect(() => {
    if (selectedBranch) {
      const agentEl = branchRefs.current.get(selectedBranch.branchId);
      agentEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedBranch]);

  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ width: '100%' }}>
        <SelectionCardsSkeleton count={4} />
      </Stack>
    );
  }

  return (
    <Stack
      direction='column'
      spacing={2}
      sx={{
        width: '100%',
        overflowY: 'auto',
        scrollbarColor: 'grey transparent',
      }}
    >
      {branches.map((branch) => {
        const isSelected = selectedBranch?.branchId === branch.branchId;

        return (
          <Stack
            direction='column'
            key={branch.branchId}
            onClick={() => onSelectBranch(branch)}
            ref={(el: HTMLDivElement | null) => {
              if (el) branchRefs.current.set(branch.branchId, el);
              else branchRefs.current.delete(branch.branchId);
            }}
            sx={[
              {
                backgroundColor: 'background.default',
                border: '1px solid',
                borderColor: 'grey.200',
                borderRadius: 2,
                padding: 3,
                width: '100%',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  borderColor: 'primary.main',
                },
              },
              isSelected && {
                backgroundColor: '#F5F9FF',
                borderColor: 'primary.main',
                borderWidth: '2px',
              },
            ]}
          >
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              sx={{ mb: 1 }}
            >
              <Icon
                iconName='location-dot-duotone'
                color='primary'
                sx={{
                  fontSize: 24,
                }}
              />
              <Typography
                variant='h5'
                sx={[
                  {
                    textAlign: 'left',
                    fontWeight: 600,
                  },
                  isSelected && { color: 'inherit' },
                ]}
              >
                {branch.branchName}
              </Typography>
            </Stack>

            <Typography
              variant='b3Regular'
              sx={{
                textAlign: 'left',
                color: 'grey.600',
              }}
            >
              {branch.distance} mi • {branch.street}, {branch.city}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default BranchSelectionList;
